import { EventDetails } from '../../../interfaces/userInterfaces';

export interface MonthDef {
  month: string;
  days: number;
  index: number;
  monthNumber: string;
}

export const MONTHS: MonthDef[] = [
  { month: 'January', days: 31, index: 0, monthNumber: '01' },
  { month: 'February', days: 28, index: 1, monthNumber: '02' },
  { month: 'March', days: 31, index: 2, monthNumber: '03' },
  { month: 'April', days: 30, index: 3, monthNumber: '04' },
  { month: 'May', days: 31, index: 4, monthNumber: '05' },
  { month: 'June', days: 30, index: 5, monthNumber: '06' },
  { month: 'July', days: 31, index: 6, monthNumber: '07' },
  { month: 'August', days: 31, index: 7, monthNumber: '08' },
  { month: 'September', days: 30, index: 8, monthNumber: '09' },
  { month: 'October', days: 31, index: 9, monthNumber: '10' },
  { month: 'November', days: 30, index: 10, monthNumber: '11' },
  { month: 'December', days: 31, index: 11, monthNumber: '12' },
];

export const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Returns the number of days in a given month, accounting for leap years.
 */
export function daysInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate();
}

/**
 * Builds a 2D array of Date | null representing a calendar grid (6 rows × 7 cols).
 * Cells outside the current month are null.
 */
export function buildCalendarGrid(year: number, monthIndex: number): (Date | null)[][] {
  const firstDayOfWeek = new Date(year, monthIndex, 1).getDay();
  const totalDays = daysInMonth(year, monthIndex);
  const grid: (Date | null)[][] = [];
  let dayCounter = 1;

  for (let week = 0; week < 6; week++) {
    const row: (Date | null)[] = [];
    for (let dow = 0; dow < 7; dow++) {
      const cellIndex = week * 7 + dow;
      if (cellIndex < firstDayOfWeek || dayCounter > totalDays) {
        row.push(null);
      } else {
        row.push(new Date(year, monthIndex, dayCounter));
        dayCounter++;
      }
    }
    // Skip entirely empty trailing weeks
    if (row.every((d) => d === null) && week > 3) break;
    grid.push(row);
  }

  return grid;
}

/**
 * Returns a Set of locale date strings ('M/D/YYYY') for all events in the list,
 * so day cells can do a fast O(1) lookup.
 */
export function buildEventDateSet(eventList: EventDetails[]): Map<string, EventDetails[]> {
  const map = new Map<string, EventDetails[]>();
  for (const event of eventList) {
    const key = new Date(`${event.date}T${event.time}`).toLocaleDateString();
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(event);
  }
  return map;
}

/**
 * Parses a YYYY-MM-DD date string safely across all browsers (Safari treats
 * ISO dates as UTC, which shifts the displayed date by one day).
 */
export function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}
